(() => {
  let sequence = 0;
  const normalise = (value = '') => String(value).trim().toLocaleLowerCase('de').replaceAll('é', 'e');
  const colourKey = (value) => {
    const text = normalise(value);
    if (/rose|rosa|rosegold/.test(text) && /gold/.test(text)) return 'rose';
    if (/gold/.test(text)) return 'gold';
    if (/silber|silver|platin/.test(text)) return 'silver';
    return text;
  };
  class LanoelCard extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const source = this.querySelector('[data-card-data]');
      if (!source) return;
      try { this.data = JSON.parse(source.textContent); } catch { return; }
      this.controller = new AbortController();
      this.track = this.dataset.gallery === 'true' ? this.querySelector('[data-card-track]') : null;
      this.slides = [...this.querySelectorAll('[data-image-id]')];
      this.colourPosition = Number(this.dataset.colourPosition);
      this.initial = this.data.variants.find(v => String(v.id) === String(this.data.initial));
      this.variant = this.initial;
      this.groups = this.groupImages();
      const signal = this.controller.signal;
      this.addEventListener('click', this.onClick, { capture: true, signal });
      if (this.track) {
        this.track.id = `ln-card-gallery-${++sequence}`;
        this.querySelectorAll('[data-card-step]').forEach(b => b.setAttribute('aria-controls', this.track.id));
        this.track.addEventListener('scroll', this.onScroll, { passive: true, signal });
        this.track.addEventListener('keydown', this.onKey, { signal });
        this.track.addEventListener('dragstart', e => e.preventDefault(), { signal });
        this.track.addEventListener('pointerdown', e => { this.pointer = {x:e.clientX,y:e.clientY}; this.dragged = false; }, { passive: true, signal });
        this.track.addEventListener('pointermove', e => {
          if (this.pointer && Math.abs(e.clientX - this.pointer.x) > 8) this.dragged = true;
        }, { passive: true, signal });
        const finish = () => {
          if (this.dragged) this.suppressUntil = performance.now() + 450;
          this.pointer = null;
        };
        this.track.addEventListener('pointerup', finish, { passive: true, signal });
        this.track.addEventListener('pointercancel', finish, { passive: true, signal });
        this.renderGallery();
      }
      this.querySelectorAll('[data-card-colour]').forEach(button => {
        const colour = button.dataset.cardColour;
        button.setAttribute('aria-pressed', String(this.variant?.options[this.colourPosition] === colour));
        const swatch = button.querySelector('.ln-swatch--other');
        if (swatch) {
          const colours = {hellorange:'#e7a578',khaki:'#aaa480',hellgrau:'#d1d1ce',dunkelbraun:'#63483e',purpur:'#782e4e',rosenrot:'#c7798b'};
          swatch.style.setProperty('--finish-colour', colours[normalise(colour)] || '#d7cfc1');
        }
      });
      this.dataset.ready = 'true';
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.frame);
    }
    groupImages() {
      const groups = new Map();
      const auditedGroups = {'42dd864e90ddef35a8c7c8f492bd12a2':['dunkelbraun'],'8c4bb7965d5c100487eb105539ef72d7':['dunkelbraun'],'794d8cbc1f73937f85a85aa2c2955f8d':['_unmatched_pink'],"a580030a7ed9262724a50452e77ba355_1080x1620_05e73ea0-4bf0-4608-9304-bd7130e4a19d": ["silver"], "8abad6bdc7a6f7217d6312e0fceb2d7b_1080x1620_01875280-881a-4e1b-bd1f-f3ca28d687cb": ["silver"], "9d41d8a4-090a-4f08-95d6-59a71e916b97": ["silver"]};
      if (this.colourPosition < 0) return groups;
      let previous = '';
      const initialColour = colourKey(this.initial?.options[this.colourPosition]);
      for (const image of this.data.images) {
        const matches = this.data.variants.filter(v => String(v.image) === String(image.id));
        let keys = [...new Set(matches.map(v => colourKey(v.options[this.colourPosition])))];
        const audited = Object.entries(auditedGroups).find(([filename]) => image.url.includes(filename));
        if (audited) keys = audited[1];
        if (!keys.length) {
          const filename = normalise(image.url.split('/').pop().split('?')[0]);
          const alt = normalise(image.alt || '').replace(/925\s+sterling\s+silber|sterling\s+silber/g,'');
          const identify = text => /(?:^|[\s_\-,])(?:silber|silver)(?=[\s_\-,.])/.test(text) ? 'silver' : /(?:^|[\s_\-,])gold(?=[\s_\-,.])/.test(text) ? 'gold' : '';
          const explicit = identify(filename) || identify(alt);
          if (explicit) keys = [explicit];
        }
        if (matches.length) previous = colourKey(matches[0].options[this.colourPosition]);
        groups.set(String(image.id), keys.length ? keys : [previous || initialColour]);
      }
      return groups;
    }
    onClick = (event) => {
      const button = event.target.closest('button');
      if (button?.hasAttribute('data-card-colour')) {
        event.stopPropagation();
        const candidates = this.data.variants.filter(v => v.options[this.colourPosition] === button.dataset.cardColour);
        const variant = candidates.find(v => v.available) || candidates[0];
        if (variant) this.selectVariant(variant);
        return;
      }
      if (button?.hasAttribute('data-card-step')) {
        event.stopPropagation();
        this.step(Number(button.dataset.cardStep));
        return;
      }
      if (event.target.closest('[data-card-track]') && performance.now() < (this.suppressUntil || 0)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    selectVariant(variant) {
      this.variant = variant;
      this.dataset.variantId = String(variant.id);
      this.dataset.variantAvailable = String(variant.available);
      const url = new URL(this.data.url, location.origin);
      url.searchParams.set('variant', variant.id);
      this.querySelectorAll('a').forEach(a => a.href = url.pathname + url.search);
      const parentLink = this.closest('product-card')?.querySelector('.product-card__link');
      if (parentLink) parentLink.href = url.pathname + url.search;
      const regularPrice = this.querySelector('[data-card-regular-price]');
      if (regularPrice) regularPrice.textContent = variant.price;
      const salePrice = this.querySelector('[data-card-sale-price] bdi');
      const wasPrice = this.querySelector('[data-card-was-price] bdi');
      if (salePrice) salePrice.textContent = variant.salePrice;
      if (wasPrice) wasPrice.textContent = variant.price;
      this.querySelectorAll('[data-card-sale]').forEach(badge => {
        badge.hidden = !variant.available;
      });
      this.querySelector('[data-card-material]').textContent = variant.material;
      this.querySelector('[data-card-sold-out]').hidden = variant.available;
      this.querySelector('[data-card-silver]').hidden = !String(variant.material || '').startsWith('925 Sterling Silber');
      this.querySelector('[data-card-stone]').hidden = variant.stone !== 'Moissanit';
      this.querySelectorAll('[data-card-colour]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.cardColour === variant.options[this.colourPosition])));
      this.querySelector('[data-card-state]').textContent = `${variant.options[this.colourPosition] || ''} · ${variant.available ? this.data.labels.available : this.data.labels.soldOut}`;
      if (this.track) this.renderGallery();
      else {
        const image = this.data.images.find(i => String(i.id) === String(variant.image));
        const target = this.querySelector('[data-card-static] img');
        if (image && target) {
          target.removeAttribute('srcset');
          target.src = image.url;
        }
      }
    }
    renderGallery() {
      if (!this.track || !this.variant) return;
      const hero = String(this.variant.image || this.data.images[0]?.id);
      const key = colourKey(this.variant.options[this.colourPosition]);
      const visible = this.slides.filter(slide => {
        const keys = this.groups.get(slide.dataset.imageId);
        return this.colourPosition < 0 || !keys || keys.includes(key) || slide.dataset.imageId === hero;
      });
      const first = visible.find(s => s.dataset.imageId === hero) || visible[0];
      this.activeSlides = first ? [first, ...visible.filter(s => s !== first)] : [];
      for (const slide of this.slides) {
        slide.hidden = !this.activeSlides.includes(slide);
        slide.tabIndex = slide === first ? 0 : -1;
      }
      this.activeSlides.forEach(slide => {
        this.track.append(slide);
        const image = slide.querySelector('[data-card-src]');
        if (image) {
          image.srcset = [240,400,600,800].map(w => `${image.dataset.cardSrc.replace(/width=\d+/, `width=${w}`)} ${w}w`).join(', ');
          image.sizes = '(min-width: 1200px) 23vw, (min-width: 750px) 32vw, 48vw';
          image.src = image.dataset.cardSrc;
          image.removeAttribute('data-card-src');
        }
      });
      this.track.scrollTo({left:0,behavior:'instant'});
      const multiple = this.activeSlides.length > 1;
      this.querySelector('[data-card-controls]').hidden = !multiple;
      this.querySelector('[data-card-position]').hidden = !multiple;
      this.updatePosition();
    }
    onScroll = () => {
      if (this.pointer) this.suppressUntil = performance.now() + 450;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updatePosition());
    };
    updatePosition() {
      if (!this.track || !this.activeSlides?.length) return;
      this.index = Math.max(0, Math.min(this.activeSlides.length - 1, Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth,1))));
      this.querySelector('[data-card-position]').textContent = `${this.index + 1} / ${this.activeSlides.length}`;
      this.querySelector('[data-card-step="-1"]').disabled = this.index === 0;
      this.querySelector('[data-card-step="1"]').disabled = this.index === this.activeSlides.length - 1;
      this.activeSlides.forEach((s,i) => s.tabIndex = i === this.index ? 0 : -1);
    }
    step(delta) {
      if (!this.track) return;
      const next = Math.max(0, Math.min(this.activeSlides.length - 1, (this.index || 0) + delta));
      this.track.scrollTo({left:next * this.track.clientWidth,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
    }
    onKey = (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(event.key === 'ArrowRight' ? 1 : -1);
      }
    };
  }
  if (!customElements.get('lanoel-card')) customElements.define('lanoel-card',LanoelCard);
})();
